import { getEnvName } from '@disco/constants/environment'
import type { MappedEventTypes } from '@disco/constants/EventTypes'
import { getEventDescription } from '@disco/constants/EventTypes'
import type { BusinessData } from 'types/business'
import type { User } from 'types/user'
const SESSION_REPLAY_IGNORE_LOCATIONS: Location[] = [
  'Libraries',
  'Share',
  'Upload',
]
const MAX_LOAD_ATTEMPTS = 3

type MixpanelBrowser = {
  identify: (id?: string) => void
  init: (token: string, config?: Record<string, unknown>) => void
  people: { set: (props: Record<string, unknown>) => void }
  register: (props: Record<string, unknown>) => void
  track: (name: string, props?: Record<string, unknown>) => void
  get_distinct_id: () => string | undefined
  start_session_recording: () => void
  stop_session_recording: () => void
}

let realMixpanel: MixpanelBrowser | null = null
let mixpanelInitialized = false
let loadPromise: Promise<MixpanelBrowser | null> | null = null
let loadAttempts = 0
let loadDisabled = false
const pendingInitCalls: Array<(mp: MixpanelBrowser) => void> = []
const pendingCalls: Array<(mp: MixpanelBrowser) => void> = []

function runSafely(run: (mp: MixpanelBrowser) => void, mp: MixpanelBrowser) {
  try {
    run(mp)
    return true
  } catch (e) {
    console.log('mixpanel error', e)
    return false
  }
}

function flushPendingCalls(mp: MixpanelBrowser) {
  if (!mixpanelInitialized) return
  for (const run of pendingCalls) runSafely(run, mp)
  pendingCalls.length = 0
}

// Read at init rather than persisted, so a language change is picked up on the
// next load. Absent values are omitted instead of registered as null.
function getBrowserLocaleProps(): {
  'Browser locale'?: string
  'Browser locales'?: string
} {
  const props = {
    ...(navigator?.language ? { 'Browser locale': navigator.language } : {}),
    ...(navigator?.languages?.length
      ? { 'Browser locales': navigator.languages.join(',') }
      : {}),
  }

  return props
}

function loadMixpanel(): Promise<MixpanelBrowser | null> {
  if (loadPromise) return loadPromise
  loadAttempts += 1
  loadPromise = import('mixpanel-browser')
    .then(mod => {
      const mp = mod.default as unknown as MixpanelBrowser
      realMixpanel = mp
      for (const run of pendingInitCalls) {
        if (runSafely(run, mp)) mixpanelInitialized = true
      }
      pendingInitCalls.length = 0
      flushPendingCalls(mp)
      return mp
    })
    .catch(e => {
      console.log('mixpanel load error', e)
      loadPromise = null
      if (loadAttempts >= MAX_LOAD_ATTEMPTS) {
        loadDisabled = true
        pendingInitCalls.length = 0
        pendingCalls.length = 0
      }
      return null
    })
  return loadPromise
}

function enqueue(run: (mp: MixpanelBrowser) => void) {
  if (realMixpanel && mixpanelInitialized) {
    runSafely(run, realMixpanel)
    return
  }
  if (loadDisabled) return
  pendingCalls.push(run)
  void loadMixpanel()
}

function initialize(run: (mp: MixpanelBrowser) => void) {
  if (realMixpanel) {
    if (runSafely(run, realMixpanel)) {
      mixpanelInitialized = true
      flushPendingCalls(realMixpanel)
    }
    return
  }
  if (loadDisabled) return
  pendingInitCalls.push(run)
  void loadMixpanel()
}

export const deferredMixpanelBrowser: MixpanelBrowser =
  typeof document !== 'undefined'
    ? {
        identify: id => enqueue(mp => mp.identify(id)),
        init: (token, config) => initialize(mp => mp.init(token, config)),
        people: { set: props => enqueue(mp => mp.people.set(props)) },
        register: props => enqueue(mp => mp.register(props)),
        track: (name, props) => enqueue(mp => mp.track(name, props)),
        get_distinct_id: () => realMixpanel?.get_distinct_id(),
        start_session_recording: () =>
          enqueue(mp => mp.start_session_recording()),
        stop_session_recording: () =>
          enqueue(mp => mp.stop_session_recording()),
      }
    : {
        identify: () => {},
        init: () => {},
        people: { set: () => {} },
        register: () => {},
        track: () => {},
        get_distinct_id: () => undefined,
        start_session_recording: () => {},
        stop_session_recording: () => {},
      }

const mixpanel = deferredMixpanelBrowser
export const tokens = {
  dev: 'c17684f12ba75092e63ed0baf1a3b6a5',
  staging: 'c17684f12ba75092e63ed0baf1a3b6a5',
  prod: 'd041f61e9c4961af3cff17d999ae1966',
  'dev-website': 'c17684f12ba75092e63ed0baf1a3b6a5',
  'staging-website': 'c17684f12ba75092e63ed0baf1a3b6a5',
  'prod-website': '3fb7fa13c64de0a15965231b8d26124a',
}
// TODO: rename type Location as name is conflicting
// TODO: remove "void |" and update all login screens to pass location
export type Location =
  | 'Login'
  | 'Accounts'
  | 'Explorer'
  | 'Libraries'
  | 'Discover Music'
  | 'Mobile'
  | 'Share'
  | 'Upload'
  | 'Website'
  | 'Settings'
  | 'Unsubscribe'
  | 'Signup'

type PrevSetupProps = {
  user: User | null | undefined
  company: BusinessData
}

const PENDING_EVENTS_FLUSH_TIMEOUT_MS = 8000

class Mixpanel {
  location: Location = 'Explorer'
  initialized: boolean = false
  prevSetupProps: Partial<PrevSetupProps> = {}
  initializationEvents: Partial<
    Record<Location, Record<string, (...args: Array<any>) => any>>
  > = {}
  businessId: number | null | undefined
  pendingEvents: Array<() => void> = []
  pendingEventsFlushTimer: ReturnType<typeof setTimeout> | null = null

  // TODO: Pass arguments as object
  setup(
    user?: User,
    company?: BusinessData,
    options: {
      location?: Location
    } = {},
  ) {
    const { location } = options

    try {
      // Set synchronously so `business_id` is available on events sent before
      // mixpanel.init's async `loaded` callback resolves (e.g. autoplay on
      // share pages, where audio can start before the SDK finishes loading).
      if (company?.id) {
        this.businessId = company.id
      }

      if (user && company) {
        this.syncUserInfo(user, company, options)
      }
      const token = company?.mixpanel_token || tokens[getEnvName()]

      mixpanel.init(token, {
        // separate cookies for different subdomains to ensure unique distinct_id's per business
        cross_subdomain_cookie: false,
        persistence: 'cookie', // Only use cookies, not localStorage
        record_mask_text_selector: '',
        // proxy MP via our own API to avoid being blocked
        api_host: '/_private/m/p/api',
        // mixpanel-browser replaces its default routes with this object instead of
        // merging, so every endpoint it calls needs a key here and a backend rewrite.
        api_routes: { track: 't/', engage: 'e/', groups: 'g/', record: 'r/' },
        loaded: () => {
          const localeProps = getBrowserLocaleProps()

          if (Object.keys(localeProps).length > 0) {
            // Register is setup before flushPendingEvents below so queued events carry
            // all super properties.
            mixpanel.register(localeProps)
          }

          if (location) {
            this.location = location
          }

          if (user && company) {
            this.setUserInfo(user, company)
          }

          this.initialized = true

          // Location and identify are both set above/in setUserInfo, so any
          // events queued while waiting for `loaded` now have the right
          // Location and distinct_id instead of the 'Explorer' default and
          // anonymous $device id.
          this.flushPendingEvents()

          if (location) {
            this.callInitializationEvents(location)
          }
          this.prevSetupProps = {
            user,
            company,
          }
          this.startSessionRecording()
        },
      })
    } catch (e) {
      console.log('mixpanel error', e)
    }
  }

  startSessionRecording(force?: boolean): void | Error {
    if (!force) {
      // Only enable session recording non public locations
      if (SESSION_REPLAY_IGNORE_LOCATIONS.includes(this.location)) return
      // Enable session recording for 5% of users
      if (Math.random() > 0.05) return
    }

    try {
      mixpanel.start_session_recording()
      console.log('Mixpanel Recording Enabled')
    } catch (e) {
      return e
    }
  }

  syncUserInfo(
    user: User | null | undefined,
    company: BusinessData,
    options: {
      location?: Location
    } = {},
  ) {
    if (!this.initialized) return
    const { location } = options
    const currentData = {
      user,
      company,
    }
    const prevData = this.prevSetupProps
    const needUpdate = currentData.user?.id !== prevData.user?.id

    if (needUpdate) {
      this.setUserInfo(user, company)
      if (location) {
        this.callInitializationEvents(location)
      }
      this.prevSetupProps = {
        user,
        company,
      }
    }

    return
  }

  setUserInfo(user: User | null | undefined, company: BusinessData) {
    if (company?.id) {
      this.businessId = company.id
    }

    if (user && user?.id) {
      mixpanel.identify(`${company?.id}-${user.id}`)
      mixpanel.people.set({
        $first_name: user.first_name,
        $last_name: user.last_name,
        $created: user.created_at,
        $email: user.email,
        Business: company.name,
        version: FRONTEND_VERSION,
      })
    }
  }

  setInitializationEvent(
    location: Location,
    event: {
      name: string
      callback: (...args: Array<any>) => any
    },
  ) {
    if (!this.initializationEvents[location]) {
      this.initializationEvents[location] = {}
    }

    this.initializationEvents[location][event.name] = event.callback
  }

  callInitializationEvents(location: Location) {
    if (!this.initializationEvents[location]) return
    const callbacks: ((...args: Array<any>) => any)[] | null | undefined =
      Object.values(this.initializationEvents[location])

    if (callbacks) {
      callbacks.forEach(callback => callback())
    }
  }

  send(event: { event: MappedEventTypes }): void {
    this.queueOrRun(() => {
      try {
        mixpanel.track(getEventDescription(event.event))
      } catch (e) {
        return e
      }
    })
  }

  mixpanelLocationOverride = (locationOverride?: Location): Location => {
    return locationOverride ?? this.location
  }

  sendPureEvent(name: string, props?: Record<string, any>): void {
    this.queueOrRun(() => {
      try {
        const { Location: updatedLocation, ...restProps } = props ?? {}

        const currentLocation = this.mixpanelLocationOverride(updatedLocation)

        mixpanel.track(name, {
          ...restProps,
          Location: currentLocation,
          business_id: this.businessId,
        })
      } catch (e) {
        return e
      }
    })
  }

  // Holds a track call back until `loaded` has set the real Location and
  // distinct_id, instead of sending it immediately with defaults/anonymous id.
  queueOrRun(run: () => void) {
    if (this.initialized) {
      run()
      return
    }

    this.pendingEvents.push(run)

    if (!this.pendingEventsFlushTimer) {
      this.pendingEventsFlushTimer = setTimeout(() => {
        this.pendingEventsFlushTimer = null
        this.dropPendingEvents()
      }, PENDING_EVENTS_FLUSH_TIMEOUT_MS)
    }
  }

  flushPendingEvents() {
    this.clearPendingEventsFlushTimer()

    const events = this.pendingEvents
    this.pendingEvents = []
    events.forEach(run => run())
  }

  dropPendingEvents() {
    this.clearPendingEventsFlushTimer()
    this.pendingEvents = []
  }

  clearPendingEventsFlushTimer() {
    if (this.pendingEventsFlushTimer) {
      clearTimeout(this.pendingEventsFlushTimer)
      this.pendingEventsFlushTimer = null
    }
  }

  /**
   * The anonymous '$device:<uuid>' id for a visitor who has not been
   * identified yet.
   *
   * Sent with the signup payload so the backend can alias it onto the business
   * user once the signup is verified. Without that the pre-signup UTM history
   * is stranded: cross_subdomain_cookie is false, so the id written on
   * accounts.disco.ac is never visible to the business subdomain.
   */
  getDistinctId(): string | undefined {
    try {
      return mixpanel.get_distinct_id?.()
    } catch {
      return undefined
    }
  }

  updateLocation(location: Location) {
    this.location = location
  }
}

const mixpanelClient = new Mixpanel()

export default mixpanelClient
